import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Footer } from "@/components/landing/Footer";
import { PageHeader } from "@/components/landing/PageHeader";

const FOUNDER = "/founder.jpg";
const ease = [0.16, 1, 0.3, 1];
const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease } },
};
const Reveal = ({ children, className = "" }) => (
  <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={reveal} className={className}>
    {children}
  </motion.div>
);
const Eyebrow = ({ children }) => (
  <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85] mb-5 flex items-center gap-3"><span className="diamond" /> {children}</p>
);
const H2 = ({ children, light }) => (
  <h2 className={`font-display font-extrabold uppercase leading-[0.95] text-4xl sm:text-5xl tracking-tight ${light ? "text-[#f5f4ee]" : ""}`}>{children}</h2>
);
const P = ({ children, light }) => (
  <p className={`text-base sm:text-lg leading-relaxed ${light ? "text-[#f5f4ee]/70" : "text-[#5f5e5a]"}`}>{children}</p>
);

const COMMITMENTS = [
  ["Faith", "Staying connected to the values that guide us."],
  ["Identity", "Being proud of who we are and where we come from."],
  ["Quality", "Creating clothing designed with attention to detail, comfort, and style."],
  ["Unity", "Bringing people together across cultures and communities."],
  ["Purpose", "Building something bigger than a clothing brand."],
];

export default function About() {
  return (
    <main data-testid="about-page" className="bg-[#ffffff] text-[#0a0a0b] min-h-screen antialiased">
      <PageHeader page="about" backLabel="Back to Drop 001" link={{ id: "shop", to: "/shop", label: "Shop" }} />

      <section className="relative pt-36 pb-20 sm:pb-28 px-5 sm:px-8 overflow-hidden">
        <div className="absolute inset-0 hero-radial" />
        <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85] mb-6 flex items-center gap-3"><span className="diamond" /> About Us</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.15 }} className="font-display font-extrabold uppercase leading-[0.9] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              Deen <span className="text-pink">District</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.3 }} className="mt-7 font-serif-accent text-2xl sm:text-3xl">
              Faith in Every Thread. Culture in Every Street.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.4 }} className="mt-8 max-w-2xl space-y-5">
              <p className="font-display font-extrabold uppercase text-base sm:text-lg tracking-wide">More Than Clothing. It's an Identity.</p>
              <P>DEEN DISTRICT is a luxury streetwear brand built on faith, culture, individuality, and purpose. Created for a generation that refuses to choose between staying true to their beliefs and expressing their personal style, DEEN DISTRICT represents the intersection of Islamic identity and modern urban fashion.</P>
              <P>We believe fashion is more than what you wear. It's a statement of who you are, where you come from, and what you stand for.</P>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease, delay: 0.25 }} className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-[#e3e2dc] bg-[#faf9f6]">
              <img data-testid="founder-photo" src={FOUNDER} alt="Jafar Rizvi, founder and designer of Deen District, sketching in the studio" className="w-full aspect-[2/3] object-cover" />
              <div className="p-5">
                <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#ff2a85]">Founder & Designer</p>
                <p className="font-display font-extrabold uppercase text-xl mt-1">Jafar Rizvi</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="about-founder" className="bg-[#f3f2ec] py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Meet the Founder</Eyebrow>
            <H2>Jafar <span className="text-pink">Rizvi</span></H2>
          </Reveal>
          <Reveal className="lg:col-span-8 space-y-5 max-w-3xl">
            <P>Born and raised in Chicago with a Muslim-American background, Jafar Rizvi is the founder, owner, and designer of DEEN DISTRICT.</P>
            <P>As an entrepreneur with a passion for creativity, business, and community, Jafar envisioned a clothing brand that would bridge the gap between his Islamic faith and the urban culture that shaped his upbringing.</P>
            <P>Growing up in Chicago, Jafar experienced the influence of street culture, music, fashion, and the diversity of American life while maintaining a strong connection to his Muslim identity. That combination became the inspiration behind DEEN DISTRICT.</P>
            <P>For Jafar, creating this brand was never simply about selling hoodies, sweatpants, or T-shirts. It was about building something meaningful — a brand that young Muslims could proudly identify with, something that represents their faith without sacrificing their love for fashion.</P>
            <blockquote className="mt-8 border-l-2 border-[#ff2a85] pl-6">
              <p className="font-serif-accent text-xl sm:text-2xl leading-snug">"I wanted to create a brand where being Muslim isn't something you have to separate from your style. Our faith is part of who we are, and DEEN DISTRICT is a way to represent that identity with confidence."</p>
              <footer className="mt-4 font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a]">— Jafar Rizvi, Founder & Designer</footer>
            </blockquote>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-why" className="py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Why</Eyebrow>
            <H2>Why Deen District <span className="text-stroke">Was Created</span></H2>
          </Reveal>
          <Reveal className="lg:col-span-8 space-y-5 max-w-3xl">
            <P>For too long, Islamic-inspired clothing has often been associated with traditional designs, while mainstream streetwear has rarely reflected the identity and values of Muslim-American youth. DEEN DISTRICT was created to help change that.</P>
            <P>Our mission is to bring together the boldness of American streetwear and the timeless values of Islam, creating premium clothing that represents confidence, brotherhood, unity, and purpose.</P>
            <P>We want Muslim men and women to feel proud of who they are, whether they're walking through the streets of Chicago, attending college, building businesses, or connecting with their communities.</P>
            <p className="font-display font-extrabold uppercase text-xl sm:text-2xl leading-tight pt-4">You shouldn't have to compromise your identity to fit into a culture.<br /><span className="text-pink">You can represent your Deen and your District.</span></p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-vision" className="bg-[#0a0a0b] text-[#f5f4ee] py-24 sm:py-32 px-5 sm:px-8 relative overflow-hidden">
        <span aria-hidden className="pointer-events-none select-none absolute -right-4 -top-6 font-display font-extrabold text-[28vw] sm:text-[18vw] leading-none text-[#f5f4ee]/[0.035]">DD</span>
        <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Our Vision</Eyebrow>
            <H2 light>Beyond <span className="text-pink">Fashion</span></H2>
          </Reveal>
          <Reveal className="lg:col-span-8 space-y-5 max-w-3xl">
            <P light>Our vision is to build DEEN DISTRICT into a globally recognized Muslim-inspired luxury streetwear brand. But our ambitions go beyond fashion.</P>
            <P light>We envision a movement that connects Muslims across cities, cultures, and backgrounds through a shared sense of identity and belonging.</P>
            <P light>Through meaningful designs, quality craftsmanship, creative collaborations, and community initiatives, we aim to inspire the next generation to embrace their faith, pursue their ambitions, and express themselves unapologetically.</P>
            <P light>We want DEEN DISTRICT to become a symbol of pride — not just in the Muslim community, but throughout the global streetwear industry.</P>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-origin" className="py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Drop 001</Eyebrow>
            <H2>The Origin — <span className="text-pink">Where It All Begins</span></H2>
          </Reveal>
          <Reveal className="lg:col-span-8 space-y-5 max-w-3xl">
            <p className="font-serif-accent text-2xl sm:text-3xl">Every movement has a beginning.</p>
            <P>Ours begins with THE ORIGIN — DROP 001, our inaugural collection. Designed to represent the foundation of DEEN DISTRICT, THE ORIGIN introduces our vision through premium oversized silhouettes, luxury-inspired essentials, and meaningful designs.</P>
            <P>Our earliest supporters, known as <span className="text-[#0a0a0b] font-semibold">The Founding 100</span>, represent the first generation of the DEEN DISTRICT movement. They aren't simply our first customers. They're part of our story.</P>
            <Link to="/shop" data-testid="about-shop-link" className="group inline-flex items-center gap-2 mt-4 px-8 py-4 rounded-full bg-[#ff2a85] text-white font-mono-d text-[11px] uppercase tracking-[0.2em] hover:bg-[#e60067] transition-colors">
              Pre-Order Drop 001 <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-commitment" className="bg-[#f3f2ec] py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <Eyebrow>Our Commitment</Eyebrow>
            <H2>At Deen District, <span className="text-stroke">we stand for</span></H2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {COMMITMENTS.map(([k, v], i) => (
              <motion.div key={k} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.08, ease }}
                className="bg-[#ffffff] border border-[#e3e2dc] rounded-2xl p-6 hover:border-[#ff2a85]/50 transition-colors">
                <span className="font-mono-d text-[10px] text-[#ff2a85] tracking-[0.25em]">0{i + 1}</span>
                <h3 className="font-display font-extrabold uppercase text-xl mt-3">{k}</h3>
                <p className="text-sm text-[#5f5e5a] leading-relaxed mt-2">{v}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-welcome" className="py-24 sm:py-32 px-5 sm:px-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 hero-radial" />
        <Reveal className="relative max-w-3xl mx-auto">
          <Eyebrow>Welcome to the District</Eyebrow>
          <P>From the streets of Chicago to the global Muslim community, DEEN DISTRICT is a reflection of faith, ambition, culture, and the courage to be different.</P>
          <p className="mt-8 font-display font-extrabold uppercase leading-[0.95] text-3xl sm:text-4xl lg:text-5xl tracking-tight">We are not here to follow every trend.<br /><span className="text-pink">We are here to create our own identity.</span></p>
          <div className="mt-12 pt-8 border-t border-[#e3e2dc]">
            <p className="font-display font-extrabold uppercase text-lg tracking-[0.2em]">Deen District</p>
            <p className="mt-2 font-serif-accent text-lg">Faith in Every Thread. Culture in Every Street.</p>
            <p className="mt-3 font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a]">Founded & Designed by Jafar Rizvi</p>
            <p className="mt-6 font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85]">More Than a Brand. A Movement.</p>
          </div>
        </Reveal>
      </section>

      <Footer />
    </main>
  );
}
